import sharedConfig from "./config.js";
import { loginSession } from "./LoginSession.jsx";
import { isMetaAuthRequest } from "./utils/index.js";
import { inRange } from "lodash";

let refreshPromise;
let tokenVersion = 0;

//! These are PMG360 backend routes. They shouldn't be hardcoded here but we're doing it for now because
//! the auth system built for the Hub wasn't written to accommodate the PMG360 backend routes that don't
//! require a JWT (these routes use an API key to authenticate).
const EXEMPT_ROUTES = ["api/v2/member-profile", "api/member-profile/data"];

/**
 * Attaches a response interceptor that automatically refreshes access tokens with
 * refresh tokens, and prompts the user to login again if the refresh token is also expired.
 * @param {import("axios").AxiosInstance} axios To attach interceptor to.
 */
export default function attachResponseInterceptor(axios) {
    // attach the token version to the request config, so that if the request fails due to expired token we can tell if the token has been refreshed since the request was made
    axios.interceptors.request.use((config) => {
        // not needed for auth/meta routes, but it's better to avoid the branch here and just attach anyway(those routes are rare)
        config.tokenVersion = tokenVersion;
        return config;
    });

    axios.interceptors.response.use(undefined, (err) => {
        let { config, response } = err;

        const isExemptRoute = EXEMPT_ROUTES.some((route) => config.url.includes(route));
        //applicability filter
        if (
            isExemptRoute ||
            !config ||
            !response?.headers ||
            //check code first because it's faster
            !inRange(response?.status, 400, 500) ||
            //check it's not itself a route for managing JWTs
            //so that we don't infinitely loop
            isMetaAuthRequest(config)
        ) {
            //do nothing
            return Promise.reject(err);
        }

        if (
            // signal from backend  in case of token expiry or blacklisting
            response.headers["token-expired"] !== "true"
        ) {
            if (response.headers["token-invalid"] === "true") {
                // signal from backend telling us we need re-auth

                // logout and redirect to login page(current url as post login redirect to get them back where they were)
                loginSession.logout(true, window.location.href);
            }
            return Promise.reject(err);
        }

        //if the token has been refreshed since the request was made, skip to retry
        if (config.tokenVersion !== tokenVersion) {
            //retry with new access token
            return axios(config);
        }

        if (!refreshPromise) {
            refreshPromise = axios
                .post(sharedConfig.centralBackendURL + "api/auth/refresh")
                .then(() => tokenVersion++)
                .catch(() => {})
                .finally(() => (refreshPromise = null));
        }

        //retry with new token
        return refreshPromise.then(() => {
            if (config.tokenVersion === tokenVersion) {
                //token could not be refreshed
                //we know because tokenVersion hasn't changed even though promise is done

                //logout and redirect to login page(current url as post login redirect to get them back where they were)
                loginSession.logout(true, window.location.href);
                return Promise.reject(err);
            } else {
                //retry with new access token
                return axios(config);
            }
        });
    });
}
